<script setup lang="ts">
import { ref, watch } from "vue";

import { getIconClasses } from "@/helpers/cssClass";

import type { IconObject } from "@/dsComponents/buttons/types";

import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";

import Overlay from "./Overlay.vue";

interface SideModalProps {
    open: boolean;
    width?: number;
    title?: string;
    titleBorder?: boolean;
    icon?: IconObject | string;
    close?: boolean;
    leadAction?: boolean;
}

const props = withDefaults(defineProps<SideModalProps>(), { open: false, width: 500, close: true, titleBorder: true });

const emit = defineEmits(["close", "clickLeadAction"]);

const closeAnimate = ref(false);
const isOpen = ref(props.open);
let closeTimeoutId: ReturnType<typeof setTimeout> | null = null;

const cancelPendingClose = () => {
    if (closeTimeoutId !== null) {
        clearTimeout(closeTimeoutId);
        closeTimeoutId = null;
    }
};

const closeFn = () => {
    cancelPendingClose();
    closeAnimate.value = true;

    closeTimeoutId = setTimeout(() => {
        closeTimeoutId = null;
        emit("close");
        closeAnimate.value = false;
        isOpen.value = false;
    }, 500);
};

watch(
    () => props.open,
    (open) => {
        if (open) {
            // Cancel any pending close animation: if the parent re-opens
            // the modal while a close is in flight, the stale setTimeout
            // would otherwise emit("close") and wipe the new state.
            cancelPendingClose();
            closeAnimate.value = false;
            isOpen.value = true;
        } else if (isOpen.value) {
            closeFn();
        }
    }
);
</script>
<template>
    <Teleport to="#teleport-modal">
        <Overlay v-show="isOpen" :fadeOut="closeAnimate" :center="false" @click="closeFn">
            <div class="ds-side-modal-component" :class="{ open, close: closeAnimate }" :style="`--side-modal-width: ${width}px`">
                <!-- Header -->
                <div v-if="title || close" class="header-side-modal" :class="{ 'title-border': titleBorder }">
                    <div class="header-lead-side-modal">
                        <ButtonAlt
                            v-if="leadAction"
                            class="headerlead-action-btn"
                            iconOnly="chevron-left"
                            type="neutral"
                            variant="ghost"
                            size="sm"
                            @click="emit('clickLeadAction')"
                        />
                        <div v-if="title" class="header-title-wrapper">
                            <i v-if="icon" :class="['header-title-icon', ...getIconClasses(icon)]" />
                            <h3 class="typo-sm-medium">{{ title }}</h3>
                            <slot name="title-buttons-group"></slot>
                        </div>
                    </div>
                    <div class="header-actions-side-modal" v-if="$slots['header-actions'] || close">
                        <slot name="header-actions"></slot>
                        <ButtonAlt v-if="close" class="close-btn" icon-only="x" type="neutral" size="sm" @click="closeFn"></ButtonAlt>
                    </div>
                </div>
                <!-- Content -->
                <div class="contenair-side-modal">
                    <slot></slot>
                </div>
                <!-- Footer -->
                <div class="footer-side-modal" v-if="$slots.footer">
                    <slot name="footer"></slot>
                </div>
            </div>
        </Overlay>
    </Teleport>
</template>
<style scoped lang="scss">
@keyframes modalSlideIn {
    from {
        transform: translateX(0);
    }

    to {
        transform: translateX(calc(var(--side-modal-width) * -1));
    }
}
@keyframes modalSlideOut {
    from {
        transform: translateX(calc(var(--side-modal-width) * -1));
    }

    to {
        transform: translateX(0);
    }
}

.ds-side-modal-component {
    width: var(--side-modal-width);
    height: 100%;
    background-color: white;
    position: fixed;
    right: calc(var(--side-modal-width) * -1);
    top: 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    .header-side-modal {
        min-height: 60px;
        display: flex;
        gap: 12px;
        padding: 16px 24px;
        align-items: center;
        justify-content: space-between;
        width: 100%;

        &.title-border {
            border-bottom: 1px solid var(--stroke-soft-200, #dbdfe6);
        }

        .header-lead-side-modal {
            display: flex;
            align-items: center;
            gap: 12px;
            flex: 1;
            min-width: 0;

            .header-title-wrapper {
                display: flex;
                align-items: center;
                gap: var(--spacing-md, 8px);
                min-width: 0;

                h3 {
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    flex: 1 0 0;
                }

                .header-title-icon {
                    flex-shrink: 0;
                    width: 16px;
                    height: 16px;
                    font-size: 16px;

                    &.yellow {
                        color: var(--yellow-base, #f59e0b);
                    }

                    &.green {
                        color: var(--green-base, #00a355);
                    }

                    &.blue {
                        color: var(--blue-base, #2051f8);
                    }

                    &.red {
                        color: var(--red-base, #ef4444);
                    }

                    &.dark-grey {
                        color: var(--text-sub-600, #494e61);
                    }

                    &.grey {
                        color: var(--neutral-500, #6b7280);
                    }
                }
            }
        }

        .header-actions-side-modal {
            gap: 2px;
            display: flex;
            align-items: center;
        }
    }

    .contenair-side-modal {
        overflow: auto;
        flex: 1 0 0;
        padding-right: 12px;
    }

    .footer-side-modal {
        min-height: 68px;
        display: flex;
        padding: 16px 24px;
        justify-content: flex-end;
        border-top: 1px solid $neutral-200;
    }

    &.open {
        animation: modalSlideIn forwards 0.5s;
    }

    &.close {
        animation: modalSlideOut forwards 0.5s;
    }
}
</style>
<style lang="scss"></style>
